import React, { useState, useEffect } from 'react';
import useReveal from '../../../shared/hooks/useReveal';

const terminalLines = [
  'connecting to Square POS…  done ✓',
  'syncing 2,847 transactions…  done ✓',
  'pulling payroll from Gusto…  done ✓',
  'calculating food cost ratio…  done ✓',
  'updating margin forecast…  done ✓',
  'syncing inventory levels…  done ✓',
  'generating weekly report…  done ✓',
  'analyzing labor efficiency…  done ✓',
];

const TerminalFeed = ({ active }) => {
  const [lines, setLines] = useState([]);

  useEffect(() => {
    if (!active) return;
    let i = 0;
    let interval;
    const startDelay = setTimeout(() => {
      interval = setInterval(() => {
        if (i >= terminalLines.length) {
          // pause then reset
          setTimeout(() => { setLines([]); i = 0; }, 1200);
          return;
        }
        setLines((prev) => [...prev.slice(-4), terminalLines[i]]);
        i++;
      }, 900);
    }, 600);
    return () => { clearTimeout(startDelay); clearInterval(interval); };
  }, [active]);

  return (
    <div className="bg-zinc-900 p-3 md:p-4 font-mono" style={{ minHeight: '120px' }}>
      <div className="text-[9px] md:text-[10px] text-zinc-600 uppercase tracking-wider mb-3">Live Sync</div>
      {lines.map((line, i) => (
        <div key={`${line}-${i}`} className="flex items-center gap-2 animate-[fadeInLine_0.3s_ease-out]">
          <span className="text-zinc-600 text-[10px]">$</span>
          <span className="text-zinc-400 text-[10px] md:text-xs">{line}</span>
        </div>
      ))}
      <div className="flex items-center gap-2 mt-0.5">
        <span className="text-zinc-600 text-[10px]">$</span>
        <span className="w-1.5 h-3 bg-zinc-500 animate-pulse" />
      </div>
    </div>
  );
};

const WhatWeDo = () => {
  const [sectionRef, sectionVisible] = useReveal();
  const [mockupRef, mockupVisible] = useReveal({ threshold: 0.1 });

  return (
    <section id="platform" className="py-28 md:py-40 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section number + line */}
        <div ref={sectionRef} className="mb-20">
          <div className={`flex items-center gap-6 mb-16 reveal from-left ${sectionVisible ? 'visible' : ''}`}>
            <span className="text-6xl md:text-8xl font-extralight text-zinc-100">01</span>
            <div className="h-px flex-1 bg-zinc-100" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Left - problem statement */}
            <div>
              <h2 className={`text-3xl md:text-[2.75rem] font-light text-zinc-900 leading-tight mb-8 reveal from-below ${sectionVisible ? 'visible' : ''}`}
                  style={{ transitionDelay: '0.15s' }}>
                Your operation is everywhere.<br />
                <span className="text-zinc-300">Your answers shouldn't be.</span>
              </h2>
              <div className={`space-y-4 reveal from-below ${sectionVisible ? 'visible' : ''}`}
                   style={{ transitionDelay: '0.3s' }}>
                <p className="text-zinc-500 text-base md:text-lg leading-relaxed">
                  Inventory in one system. Scheduling in another. Finances in a spreadsheet.
                  Intuition filling the gaps. Sound familiar?
                </p>
                <p className="text-zinc-500 text-base md:text-lg leading-relaxed">
                  Arbcor connects to the tools you already use and turns scattered data into
                  clarity. Not another dashboard to check — a single source of truth.
                </p>
              </div>
            </div>

            {/* Right - three principles */}
            <div className={`flex flex-col justify-center reveal from-right ${sectionVisible ? 'visible' : ''}`}
                 style={{ transitionDelay: '0.4s' }}>
              {[
                { num: '01', text: 'Connect your existing tools — no rip-and-replace' },
                { num: '02', text: 'See real-time insights across your entire operation' },
                { num: '03', text: 'Make confident decisions backed by your own data' },
              ].map((item) => (
                <div key={item.num} className="flex items-start gap-5 py-5 border-b border-zinc-100 last:border-0">
                  <span className="text-xs text-zinc-300 font-medium tracking-wider mt-1">{item.num}</span>
                  <span className="text-zinc-700 text-base md:text-lg font-light">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Dashboard mockup - enters differently */}
        <div ref={mockupRef} className={`reveal scale-in ${mockupVisible ? 'visible' : ''}`}>
          <div className="bg-zinc-950 p-3 md:p-4">
            {/* Window chrome */}
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-zinc-700" />
              <div className="w-2 h-2 rounded-full bg-zinc-800" />
              <div className="w-2 h-2 rounded-full bg-zinc-800" />
              <div className="ml-4 h-5 w-48 bg-zinc-900 rounded-sm" />
            </div>

            {/* Dashboard content */}
            <div className="grid grid-cols-12 gap-2 md:gap-3">
              {/* Sidebar */}
              <div className="col-span-2 bg-zinc-900 p-3 hidden md:block">
                <div className="w-8 h-1 bg-zinc-700 mb-6" />
                {[...Array(5)].map((_, i) => (
                  <div key={i} className={`h-1 mb-4 ${i === 0 ? 'w-full bg-zinc-600' : 'w-3/4 bg-zinc-800'}`} />
                ))}
              </div>

              {/* Main content */}
              <div className="col-span-12 md:col-span-10 space-y-2 md:space-y-3">
                {/* Metric row */}
                <div className="grid grid-cols-4 gap-2 md:gap-3">
                  {[
                    { label: 'Revenue', val: '$94.2K', delta: '+12.4%' },
                    { label: 'Operations', val: '1,847', delta: 'Active' },
                    { label: 'Efficiency', val: '97.1%', delta: '+3.2%' },
                    { label: 'Uptime', val: '99.9%', delta: 'Stable' },
                  ].map((m) => (
                    <div key={m.label} className="bg-zinc-900 p-2 md:p-3">
                      <div className="text-[8px] md:text-[10px] text-zinc-600 uppercase tracking-wider">{m.label}</div>
                      <div className="text-sm md:text-lg font-medium text-zinc-200 mt-0.5">{m.val}</div>
                      <div className="text-[8px] md:text-[10px] text-zinc-500">{m.delta}</div>
                    </div>
                  ))}
                </div>

                {/* Chart + Terminal side by side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
                  <div className="bg-zinc-900 p-3 md:p-4">
                    <div className="text-[9px] md:text-[10px] text-zinc-600 uppercase tracking-wider mb-4">Performance</div>
                    <div className="flex items-end gap-[3px] md:gap-1 h-20 md:h-28">
                      {[35, 50, 40, 65, 55, 80, 60, 75, 50, 90, 70, 85, 65, 95, 75, 88, 70, 82, 78, 92, 85, 88, 80, 95].map((h, i) => (
                        <div
                          key={i}
                          className="flex-1 bg-zinc-100 rounded-[1px]"
                          style={{
                            height: `${h}%`,
                            opacity: 0.15 + (h / 95) * 0.85,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                  <TerminalFeed active={mockupVisible} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatWeDo;
